/**
 * MessageView — Full-fidelity email renderer for the Message tab.
 *
 * Displays the message body for the selected interaction (matched by id),
 * rendered with full HTML email support (images, formatting, attachments).
 * Each Interaction in the data model corresponds 1:1 with a Message, so the
 * Message tab always shows the body of the row the operator clicked —
 * inbound or outbound. Sibling messages in the same thread are surfaced
 * via the History tab.
 *
 * Uses a sandboxed iframe to safely render arbitrary HTML email content
 * while preventing script execution and external navigation.
 */

import {
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Paperclip, Image } from 'lucide-react'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useInboxStore, selectCurrentDetail } from '../store'
import type { Interaction, Message, MessageAttachment } from '../types'
import { formatUnixSeconds } from '../dateUtils'
import {
  HtmlEmailFrame,
  AttachmentChip,
  AttachmentPreviewModal,
  formatSize,
} from './MessageRenderers'

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Pick the message that corresponds to the selected interaction.
 *
 * Exported so the customer-history drill-down (CompleteHistoryTab) can resolve
 * the right message for the row the operator expanded.
 *
 * The backend stores one Interaction document per message (each row in the
 * inbox list is a single message), and `fetchThreadMessages(id)` returns
 * the selected interaction first followed by its thread siblings. The
 * selected interaction's own message has `message.id === interaction.id`,
 * so an exact id match is the correct lookup — it shows the row the
 * operator clicked regardless of direction.
 *
 * Fallback: if no id match exists (e.g. partial data), fall back to the
 * most recent message overall. We deliberately do NOT prefer one direction
 * over the other in the fallback so an outbound row never silently
 * renders an inbound body.
 */
export function getMessageForInteraction(
  messages: Message[],
  interaction: Interaction | undefined,
): Message | null {
  if (messages.length === 0) return null

  if (interaction) {
    const own = messages.find((m) => m.id === interaction.id)
    if (own) return own
  }

  // Fallback: most recent message overall.
  const sorted = [...messages].sort((a, b) => b.received_at - a.received_at)
  return sorted[0] ?? null
}

// ── Component ────────────────────────────────────────────────────────────────

export function MessageView(): ReactNode {
  const detail = useInboxStore(selectCurrentDetail)
  const detailLoading = useInboxStore((s) => s.detailLoading)
  const detailError = useInboxStore((s) => s.detailError)

  const messages = detail?.messages ?? []
  const message = getMessageForInteraction(messages, detail?.interaction)

  // Loading state
  if (detailLoading && !message) {
    return (
      <div style={styles.loadingContainer}>
        <Skeleton style={{ width: '40%', height: '14px' }} />
        <Skeleton style={{ width: '100%', height: '300px', marginTop: '12px' }} />
      </div>
    )
  }

  // Error state
  if (detailError && !message) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Failed to load message: {detailError}</p>
      </div>
    )
  }

  // Empty state
  if (!message) {
    return (
      <div style={styles.emptyContainer}>
        <p style={styles.emptyText}>No message content available.</p>
        <p style={styles.emptySubtext}>
          The interaction was received but no message body is loaded.
        </p>
      </div>
    )
  }

  const htmlContent = message.html_body || message.body_html || ''
  const textContent = message.body || message.body_text || ''
  const hasHtml = !!htmlContent
  const attachments = message.attachments ?? []
  const [previewAttachment, setPreviewAttachment] = useState<MessageAttachment | null>(null)

  return (
    <div style={styles.container}>
      {/* Message header — sender and timestamp */}
      <div style={styles.messageHeader}>
        <span style={styles.sender}>
          {message.from}
        </span>
        <span style={styles.timestamp}>
          {formatUnixSeconds(message.received_at, 'MMM d, h:mm a')}
        </span>
      </div>

      {/* Attachments bar */}
      {attachments.length > 0 && (
        <div style={styles.attachmentsBar}>
          <div style={styles.attachmentsHeader}>
            <Paperclip size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <span style={styles.attachmentsLabel}>
              {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div style={styles.attachmentsList}>
            {attachments.map((att) => (
              <AttachmentChip key={att.id} attachment={att} onPreview={setPreviewAttachment} />
            ))}
          </div>
        </div>
      )}

      {/* Message body — full-width email renderer */}
      <div style={styles.bodyContainer}>
        {hasHtml ? (
          <HtmlEmailFrame html={htmlContent} />
        ) : (
          <div style={styles.plainTextBody}>
            {textContent}
          </div>
        )}
      </div>

      {/* Inline image attachments — rendered below the email body */}
      {(() => {
        const imageAttachments = attachments.filter(
          (att) => att.content_type?.startsWith('image/') && att.url
        )
        if (imageAttachments.length === 0) return null
        return (
          <div style={styles.inlineImagesSection}>
            <div style={styles.inlineImagesHeader}>
              <Image size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <span style={styles.inlineImagesLabel}>
                {imageAttachments.length} image{imageAttachments.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={styles.inlineImagesGrid}>
              {imageAttachments.map((att) => (
                <button
                  key={att.id}
                  type="button"
                  style={styles.inlineImageCard}
                  onClick={() => setPreviewAttachment(att)}
                  title={`${att.filename} — click to preview`}
                >
                  <img
                    src={att.url}
                    alt={att.filename}
                    style={styles.inlineImageImg}
                  />
                  <span style={styles.inlineImageCaption}>
                    {att.filename}
                    <span style={styles.inlineImageSize}>{formatSize(att.size)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Attachment preview modal */}
      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  loadingContainer: {
    padding: '20px 16px',
  },
  errorContainer: {
    padding: '20px 16px',
  },
  errorText: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--error)',
  },
  emptyContainer: {
    padding: '24px 20px',
    textAlign: 'center',
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  emptySubtext: {
    marginTop: '6px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  sender: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  timestamp: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  attachmentsBar: {
    padding: '10px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  attachmentsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
  },
  attachmentsLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  attachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  bodyContainer: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 10px',
  },
  plainTextBody: {
    padding: '16px',
    fontSize: '14px',
    lineHeight: 1.7,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  inlineImagesSection: {
    padding: '12px 16px 16px',
    borderTop: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  inlineImagesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '10px',
  },
  inlineImagesLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  inlineImagesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  inlineImageCard: {
    display: 'inline-flex',
    flexDirection: 'column',
    background: 'transparent',
    border: '1px solid var(--border-subtle)',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    transition: 'border-color var(--transition-fast)',
    alignSelf: 'flex-start',
  },
  inlineImageImg: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
  },
  inlineImageCaption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  inlineImageSize: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
}

export default MessageView
